import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { partnerHealthService } from '../../../services/partnerProfileService';

const PartnerHealthDashboard = ({ healthScore, partnerId }) => {
  const [trainings, setTrainings] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (partnerId) loadHealthData();
  }, [partnerId]);

  const loadHealthData = async () => {
    setLoading(true);
    try {
      const { trainings: t, lifecycle, audit } = await partnerHealthService?.getHealthData(partnerId);
      setTrainings(t || []);

      // Build activity feed from lifecycle + audit
      const activities = [];
      lifecycle?.slice(0, 3)?.forEach(item => {
        activities?.push({
          icon: 'RefreshCw',
          action: `Lifecycle: ${item?.request_type || item?.new_status || 'Status change'}`,
          time: formatRelativeTime(item?.created_at)
        });
      });
      audit?.slice(0, 3)?.forEach(item => {
        activities?.push({
          icon: 'Activity',
          action: `${item?.action || 'Updated'}: ${item?.entity_type || 'record'}`,
          time: formatRelativeTime(item?.created_at)
        });
      });
      setRecentActivity(activities?.slice(0, 5));
    } catch (err) {
      console.error('Error loading health data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return 'Recently';
    const diff = Date.now() - new Date(dateStr)?.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (mins > 0) return `${mins} min${mins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-destructive';
  };

  // Compute capability scores from trainings
  const computeCapabilities = () => {
    if (!trainings?.length) {
      return [
        { name: 'Technical', score: 0, icon: 'Code', color: 'from-blue-500 to-cyan-500' },
        { name: 'Sales', score: 0, icon: 'TrendingUp', color: 'from-green-500 to-emerald-500' },
        { name: 'Support', score: 0, icon: 'Headphones', color: 'from-purple-500 to-pink-500' },
        { name: 'Marketing', score: 0, icon: 'Megaphone', color: 'from-orange-500 to-amber-500' }
      ];
    }
    const avgProgress = trainings?.reduce((sum, t) => sum + (t?.progress || 0), 0) / trainings?.length;
    return [
      { name: 'Technical', score: Math.min(100, Math.round(avgProgress * 1.1)), icon: 'Code', color: 'from-blue-500 to-cyan-500' },
      { name: 'Sales', score: Math.min(100, Math.round(avgProgress * 0.95)), icon: 'TrendingUp', color: 'from-green-500 to-emerald-500' },
      { name: 'Support', score: Math.min(100, Math.round(avgProgress * 0.85)), icon: 'Headphones', color: 'from-purple-500 to-pink-500' },
      { name: 'Marketing', score: Math.min(100, Math.round(avgProgress * 0.9)), icon: 'Megaphone', color: 'from-orange-500 to-amber-500' }
    ];
  };

  const capabilityScores = computeCapabilities();

  return (
    <div className="space-y-24 sticky top-24">
      {/* Health Score Card */}
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-24">
        <div className="flex items-center gap-12 mb-20">
          <div className="p-10 bg-gradient-to-br from-primary to-secondary rounded-lg">
            <Icon name="Activity" size={20} className="text-white" />
          </div>
          <h3 className="text-lg font-bold text-slate-900">Partner Health</h3>
        </div>

        <div className="flex items-center justify-center mb-20">
          <div className="relative w-32 h-32">
            <svg className="w-full h-full transform -rotate-90">
              <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="none" className="text-slate-200" />
              <circle
                cx="64" cy="64" r="56"
                stroke="currentColor" strokeWidth="12" fill="none"
                strokeDasharray={`${2 * Math.PI * 56}`}
                strokeDashoffset={`${2 * Math.PI * 56 * (1 - (healthScore || 0) / 100)}`}
                className={getScoreColor(healthScore)}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-3xl font-bold ${getScoreColor(healthScore)}`}>{healthScore || 0}</span>
              <span className="text-xs text-slate-500">Score</span>
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className={`inline-flex items-center gap-6 px-16 py-8 rounded-full text-sm font-semibold ${
            (healthScore || 0) >= 80 ? 'bg-success/10 text-success' :
            (healthScore || 0) >= 60 ? 'bg-warning/10 text-warning': 'bg-destructive/10 text-destructive'
          }`}>
            <Icon name={(healthScore || 0) >= 60 ? 'TrendingUp' : 'TrendingDown'} size={14} />
            {(healthScore || 0) >= 80 ? 'Excellent' : (healthScore || 0) >= 60 ? 'Good' : 'Needs Attention'}
          </div>
        </div>
      </div>

      {/* Capability Scores */}
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-24">
        <div className="flex items-center gap-12 mb-20">
          <div className="p-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
            <Icon name="Target" size={20} className="text-white" />
          </div>
          <h3 className="text-lg font-bold text-slate-900">Capabilities</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-16">
            {capabilityScores?.map((capability) => (
              <div key={capability?.name}>
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-8">
                    <Icon name={capability?.icon} size={16} className="text-slate-600" />
                    <span className="text-sm font-medium text-slate-700">{capability?.name}</span>
                  </div>
                  <span className={`text-sm font-bold ${getScoreColor(capability?.score)}`}>{capability?.score}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${capability?.color} rounded-full transition-all duration-500`}
                    style={{ width: `${capability?.score}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Training Status */}
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-24">
        <div className="flex items-center gap-12 mb-20">
          <div className="p-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg">
            <Icon name="GraduationCap" size={20} className="text-white" />
          </div>
          <h3 className="text-lg font-bold text-slate-900">Training</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
          </div>
        ) : trainings?.length > 0 ? (
          <div className="space-y-16">
            {trainings?.slice(0, 4)?.map((training, index) => (
              <div key={training?.id || index} className="pb-16 border-b border-slate-100 last:border-0 last:pb-0">
                <div className="flex items-center justify-between mb-8">
                  <span className="text-sm font-medium text-slate-700 truncate pr-8">{training?.name}</span>
                  <span className={`text-xs font-semibold px-8 py-4 rounded-full flex-shrink-0 ${
                    training?.progress >= 100 ? 'bg-success/10 text-success' : 'bg-primary/10 text-primary'
                  }`}>
                    {training?.progress || 0}%
                  </span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      training?.progress >= 100 ? 'bg-success' : 'bg-primary'
                    }`}
                    style={{ width: `${training?.progress || 0}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-8">No training records yet</p>
        )}
      </div>

      {/* Recent Activity */}
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-24">
        <div className="flex items-center gap-12 mb-20">
          <div className="p-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg">
            <Icon name="Clock" size={20} className="text-white" />
          </div>
          <h3 className="text-lg font-bold text-slate-900">Recent Activity</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
          </div>
        ) : recentActivity?.length > 0 ? (
          <div className="space-y-16">
            {recentActivity?.map((activity, index) => (
              <div key={index} className="flex items-start gap-12">
                <div className="p-8 bg-slate-100 rounded-lg mt-2">
                  <Icon name={activity?.icon} size={14} className="text-slate-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-700 mb-4">{activity?.action}</p>
                  <span className="text-xs text-slate-500">{activity?.time}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-8">No recent activity</p>
        )}
      </div>
    </div>
  );
};

export default PartnerHealthDashboard;